import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BillingMetricsCards from './components/BillingMetricsCards';
import BillingTabs from './components/BillingTabs';
import BillingFilters from './components/BillingFilters';
import BillingTable from './components/BillingTable';
import RevenueChart from './components/RevenueChart';
import BulkActionsBar from './components/BulkActionsBar';

const BillingManagement = () => {
  const [activeTab, setActiveTab] = useState('per-card');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    dateRange: 'this-month',
    paymentStatus: 'all',
    billingCycle: 'all',
    amountMin: '',
    amountMax: ''
  });

  // Mock billing data
  const mockBillingData = {
    'per-card': [
      {
        id: 'PC001',
        invoiceId: 'INV-2024-001',
        cardId: 'NFC-001',
        cardName: 'John Doe Business Card',
        amount: 29.99,
        status: 'paid',
        dueDate: '2024-01-15',
        paymentMethod: 'credit-card',
        billingCycle: 'monthly'
      },
      {
        id: 'PC002',
        invoiceId: 'INV-2024-002',
        cardId: 'NFC-002',
        cardName: 'Jane Smith Contact Card',
        amount: 49.99,
        status: 'pending',
        dueDate: '2024-01-20',
        paymentMethod: 'bank-transfer',
        billingCycle: 'quarterly'
      },
      {
        id: 'PC003',
        invoiceId: 'INV-2024-003',
        cardId: 'NFC-003',
        cardName: 'Marketing Team Card',
        amount: 19.99,
        status: 'overdue',
        dueDate: '2024-01-10',
        paymentMethod: 'paypal',
        billingCycle: 'monthly'
      },
      {
        id: 'PC004',
        invoiceId: 'INV-2024-004',
        cardId: 'NFC-004',
        cardName: 'Sales Executive Card',
        amount: 39.99,
        status: 'paid',
        dueDate: '2024-01-25',
        paymentMethod: 'stripe',
        billingCycle: 'monthly'
      },
      {
        id: 'PC005',
        invoiceId: 'INV-2024-005',
        cardId: 'NFC-005',
        cardName: 'Event Coordinator Card',
        amount: 59.99,
        status: 'failed',
        dueDate: '2024-01-18',
        paymentMethod: 'credit-card',
        billingCycle: 'yearly'
      }
    ],
    'per-user': [
      {
        id: 'PU001',
        invoiceId: 'INV-2024-101',
        userName: 'John Doe',
        userEmail: 'john.doe@company.com',
        amount: 89.97,
        status: 'paid',
        dueDate: '2024-01-15',
        paymentMethod: 'credit-card',
        billingCycle: 'monthly'
      },
      {
        id: 'PU002',
        invoiceId: 'INV-2024-102',
        userName: 'Jane Smith',
        userEmail: 'jane.smith@company.com',
        amount: 149.99,
        status: 'pending',
        dueDate: '2024-01-20',
        paymentMethod: 'bank-transfer',
        billingCycle: 'quarterly'
      },
      {
        id: 'PU003',
        invoiceId: 'INV-2024-103',
        userName: 'Mike Johnson',
        userEmail: 'mike.johnson@company.com',
        amount: 59.98,
        status: 'overdue',
        dueDate: '2024-01-12',
        paymentMethod: 'paypal',
        billingCycle: 'monthly'
      },
      {
        id: 'PU004',
        invoiceId: 'INV-2024-104',
        userName: 'Sarah Wilson',
        userEmail: 'sarah.wilson@company.com',
        amount: 199.99,
        status: 'paid',
        dueDate: '2024-01-28',
        paymentMethod: 'stripe',
        billingCycle: 'yearly'
      }
    ],
    'team-billing': [
      {
        id: 'TB001',
        invoiceId: 'INV-2024-201',
        teamName: 'Marketing Department',
        memberCount: 12,
        amount: 359.88,
        status: 'paid',
        dueDate: '2024-01-15',
        paymentMethod: 'bank-transfer',
        billingCycle: 'monthly'
      },
      {
        id: 'TB002',
        invoiceId: 'INV-2024-202',
        teamName: 'Sales Team',
        memberCount: 8,
        amount: 599.92,
        status: 'pending',
        dueDate: '2024-01-22',
        paymentMethod: 'credit-card',
        billingCycle: 'quarterly'
      },
      {
        id: 'TB003',
        invoiceId: 'INV-2024-203',
        teamName: 'Development Team',
        memberCount: 15,
        amount: 449.85,
        status: 'overdue',
        dueDate: '2024-01-08',
        paymentMethod: 'stripe',
        billingCycle: 'monthly'
      },
      {
        id: 'TB004',
        invoiceId: 'INV-2024-204',
        teamName: 'HR Department',
        memberCount: 5,
        amount: 149.95,
        status: 'paid',
        dueDate: '2024-01-30',
        paymentMethod: 'paypal',
        billingCycle: 'monthly'
      }
    ]
  };

  useEffect(() => {
    applyFilters();
  }, [activeTab, filters, searchQuery]);

  const applyFilters = () => {
    let data = mockBillingData[activeTab] || [];

    // Apply search filter
    if (searchQuery.trim()) {
      data = data.filter(record => {
        const searchLower = searchQuery.toLowerCase();
        return (
          record.invoiceId.toLowerCase().includes(searchLower) ||
          (record.cardId && record.cardId.toLowerCase().includes(searchLower)) ||
          (record.cardName && record.cardName.toLowerCase().includes(searchLower)) ||
          (record.userName && record.userName.toLowerCase().includes(searchLower)) ||
          (record.userEmail && record.userEmail.toLowerCase().includes(searchLower)) ||
          (record.teamName && record.teamName.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply status filter
    if (filters.paymentStatus !== 'all') {
      data = data.filter(record => record.status === filters.paymentStatus);
    }

    // Apply billing cycle filter
    if (filters.billingCycle !== 'all') {
      data = data.filter(record => record.billingCycle === filters.billingCycle);
    }

    // Apply amount range filter
    if (filters.amountMin) {
      data = data.filter(record => record.amount >= parseFloat(filters.amountMin));
    }
    if (filters.amountMax) {
      data = data.filter(record => record.amount <= parseFloat(filters.amountMax));
    }

    setFilteredData(data);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedRecords([]);
  };

  const handleSelectRecord = (recordId) => {
    setSelectedRecords(prev => {
      if (prev.includes(recordId)) {
        return prev.filter(id => id !== recordId);
      } else {
        return [...prev, recordId];
      }
    });
  };

  const handleBulkAction = (actionId) => {
    console.log(`Performing bulk action: ${actionId} on records:`, selectedRecords);
    // Implement bulk action logic here
    setSelectedRecords([]);
  };

  const handleClearSelection = () => {
    setSelectedRecords([]);
  };

  const handleGenerateInvoice = () => {
    console.log('Generating invoice...');
    // Implement invoice generation logic
  };

  const handleProcessPayment = () => {
    console.log('Processing payment...');
    // Implement payment processing logic
  };

  const handleSendReminder = () => {
    console.log('Sending reminder...');
    // Implement reminder sending logic
  };

  const handleExport = () => {
    console.log('Exporting data...');
    // Implement export functionality
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />
      
      <main className="lg:ml-60 pt-16">
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-text-primary mb-2">
              Billing Management
            </h1>
            <p className="text-text-muted">
              Comprehensive financial oversight and payment processing for NFC card operations
            </p>
          </div>

          {/* Billing Metrics */}
          <BillingMetricsCards />

          {/* Revenue Chart */}
          <RevenueChart />

          {/* Billing Tabs */}
          <BillingTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onGenerateInvoice={handleGenerateInvoice}
            onProcessPayment={handleProcessPayment}
            onSendReminder={handleSendReminder}
          />

          {/* Filters */}
          <BillingFilters
            onFilterChange={handleFilterChange}
            onExport={handleExport}
            onSearch={handleSearch}
          />

          {/* Billing Table */}
          <BillingTable
            activeTab={activeTab}
            data={filteredData}
            onSelectRecord={handleSelectRecord}
            selectedRecords={selectedRecords}
          />

          {/* Bulk Actions Bar */}
          <BulkActionsBar
            selectedCount={selectedRecords.length}
            onBulkAction={handleBulkAction}
            onClearSelection={handleClearSelection}
          />
        </div>
      </main>
    </div>
  );
};

export default BillingManagement;